// src/components/AddUpcomingProductForm.jsx
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Upload, Button, message, Spin } from "antd";
import { UploadOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Option } = Select;

/**
 * Minimal Add/Edit form for Upcoming Product with Image upload
 * Fields: name (required), collection, status (New Arrivals), image upload
 *
 * Props:
 * - open (bool)
 * - onClose (fn)
 * - onSuccess (fn)
 * - initialValues (object) optional: { id, name, collection, status, image_url }
 */
const AddUpcomingProductForm = ({ open, onClose, onSuccess, initialValues }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // local image file (File) chosen by user
  const [uploadFile, setUploadFile] = useState(null);
  // preview source: either local data URL or initialValues.image_url
  const [preview, setPreview] = useState(null);
  // when editing, user may remove existing remote image without uploading a replacement
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  // show big preview modal
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // loading state for the big preview image
  const [previewLoading, setPreviewLoading] = useState(true);

  // set initial form values & image preview when editing
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        name: initialValues.name ?? "",
        collection: initialValues.collection ?? "",
        status: initialValues.status ?? "New Arrivals",
      });

      if (initialValues.image_url) {
        setPreview(initialValues.image_url);
        setUploadFile(null);
        setRemoveExistingImage(false);
      } else {
        setPreview(null);
        setUploadFile(null);
        setRemoveExistingImage(false);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ status: "New Arrivals" });
      setUploadFile(null);
      setPreview(null);
      setRemoveExistingImage(false);
    }
  }, [initialValues, form, open]);

  // called by Upload beforeUpload: read file locally and prevent auto upload
  const handleImageChange = (file) => {
    if (!file) return false;

    // Basic client-side validations (size, type) can be added here if required
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      setUploadFile(file);
      setRemoveExistingImage(false);
    };
    reader.readAsDataURL(file);

    // Prevent antd from doing automatic upload
    return false;
  };

  const handleRemoveImage = () => {
    // If there was an existing server image and user removes it,
    // we want to inform backend to delete it (removeExistingImage true)
    if (initialValues && initialValues.image_url) {
      setRemoveExistingImage(true);
    } else {
      setRemoveExistingImage(false);
    }
    setUploadFile(null);
    setPreview(null);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // We'll always send FormData because image may be present or removed flag required
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("collection", values.collection ?? "");
      fd.append("status", values.status ?? "New Arrivals");

      if (uploadFile) {
        // uploadFile is a File from the beforeUpload handler
        fd.append("image", uploadFile);
      }

      // If user removed an existing image without uploading replacement, tell backend
      if (removeExistingImage && !uploadFile) {
        fd.append("remove_image", "1");
      }

      if (initialValues && initialValues.id) {
        await axiosInstance.put(`/admin/upcoming-products/${initialValues.id}`, fd);
        message.success("Product updated successfully");
      } else {
        await axiosInstance.post(`/admin/upcoming-products`, fd);
        message.success("Product created successfully");
      }

      form.resetFields();
      setUploadFile(null);
      setPreview(null);
      setRemoveExistingImage(false);
      onClose?.();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err.message || "Failed to save product";
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // open preview modal and ensure spinner shows until image loaded
  const openPreviewModal = (e) => {
    if (e) e.stopPropagation();
    setPreviewLoading(true);
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewLoading(true);
  };

  return (
    <>
      <Modal
        open={open}
        title={<div className="text-xl font-semibold text-gray-900">{initialValues ? "Edit Upcoming Product" : "Add Upcoming Product"}</div>}
        onCancel={() => {
          onClose?.();
          form.resetFields();
          setUploadFile(null);
          setPreview(null);
          setRemoveExistingImage(false);
        }}
        onOk={submit}
        okText={initialValues ? "Update Product" : "Create Product"}
        okButtonProps={{
          size: "large",
          className: "bg-blue-600 hover:bg-blue-700",
          loading: submitting,
        }}
        cancelButtonProps={{ size: "large" }}
        confirmLoading={submitting}
        maskClosable={false}
        width={640}
        centered
        className="upcoming-product-modal"
      >
        <Form layout="vertical" form={form} className="mt-4" requiredMark={false}>
          {/* Image Upload */}
          <Form.Item label={<span className="font-medium text-gray-700">Product Image</span>}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <Upload.Dragger
                  accept="image/*"
                  beforeUpload={(file) => handleImageChange(file)}
                  showUploadList={false}
                  multiple={false}
                  className="product-image-dragger"
                  style={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 8,
                  }}
                >
                  {preview ? (
                    <div style={{ width: "100%", position: "relative" }}>
                      <img
                        src={preview}
                        alt="Preview"
                        style={{ width: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 8, background: "#fff" }}
                      />

                      <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 8 }}>
                        <Button
                          icon={<EyeOutlined />}
                          onClick={openPreviewModal}
                          size="small"
                          title="View larger"
                        />
                        <Button
                          icon={<DeleteOutlined />}
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          size="small"
                          title="Remove"
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 24, textAlign: "center" }}>
                      <div style={{ fontSize: 34, color: "#9CA3AF", marginBottom: 12 }}>
                        <UploadOutlined />
                      </div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Click or drag image to upload</div>
                      <div style={{ color: "#6B7280", fontSize: 13 }}>Recommended: 1200×800px, JPG/PNG, max 5MB</div>
                    </div>
                  )}
                </Upload.Dragger>
              </div>

              <div style={{ width: 260 }}>
                <div style={{ background: "#F0F9FF", border: "1px solid #E0F2FE", borderRadius: 8, padding: 12 }}>
                  <h4 style={{ margin: 0, marginBottom: 8, color: "#0369A1" }}>Image Guidelines</h4>
                  <ul style={{ margin: 0, paddingLeft: 16, color: "#075985", fontSize: 13 }}>
                    <li>High quality photos</li>
                    <li>Clean background preferred</li>
                    <li>Minimum width: 800px</li>
                    <li>Max file size: 5MB</li>
                    <li>Formats: JPG, PNG, WebP</li>
                  </ul>
                </div>
              </div>
            </div>
          </Form.Item>

          {/* Minimal fields */}
          <Form.Item
            name="name"
            label={<span className="font-medium text-gray-700">Product Name <span style={{ color: "#E11D48" }}>*</span></span>}
            rules={[{ required: true, message: "Please enter product name" }]}
          >
            <Input size="large" placeholder="Enter product name" />
          </Form.Item>

          <Form.Item name="collection" label={<span className="font-medium text-gray-700">Collection / Product</span>}>
            <Input size="large" placeholder="Collection (optional)" />
          </Form.Item>

          <Form.Item name="status" label={<span className="font-medium text-gray-700">Status</span>} initialValue="New Arrivals">
            <Select size="large">
              <Option value="New Arrivals">New Arrivals</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ✅ FULL-BLEED IMAGE PREVIEW MODAL */}
      <Modal
        open={isPreviewModalOpen}
        onCancel={closePreviewModal}
        footer={null}
        centered
        destroyOnClose
        maskClosable
        width={720}
        bodyStyle={{
          padding: 0,
          background: "#000",
          borderRadius: 12,
          overflow: "hidden",
        }}
        closeIcon={
          <div
            style={{
              background: "rgba(0,0,0,0.85)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 16,
            }}
          >
            ✕
          </div>
        }
      >
        <div
          style={{
            width: "100%",
            height: "70vh", // ✅ NORMAL HEIGHT
            maxHeight: "80vh",
            background: "#000",
            position: "relative",
          }}
        >
          {/* Loader */}
          {previewLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
              }}
            >
              <Spin size="large" />
            </div>
          )}

          {/* Image */}
          {preview && (
            <img
              src={preview}
              alt="Preview"
              onLoad={() => setPreviewLoading(false)}
              onError={() => {
                setPreviewLoading(false);
                message.error("Failed to load image");
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover", // ✅ THIS FIXES BLACK AREA
                display: previewLoading ? "none" : "block",
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default AddUpcomingProductForm;
